import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { CardControl, CardControlRadio, labelText } from '../../../shared/models/CardControl';
import { ActionCenterRedirectService } from '../../../shared/services/ActionCenterRedirect.service';
import { StandingAuthorizationCard } from '../../../shared/components/standing-authorization-card-container/standing-authorization-card-container.component';
import { StandingInstruction } from '../../../shared/types/StandingInstruction';
import { transactionDirection } from '../../../shared/utilities/Constants';
import { selectStandingAuthId } from '../../reducers';
import { selectEnableUseSloaContainer } from '../../../feature-flags/reducers';

@Component({
  standalone: false,
  selector: 'mm-ach-source-of-funds',
  templateUrl: './ach-source-of-funds.component.html',
  styleUrls: ['./ach-source-of-funds.component.scss']  
})
export class AchSourceOfFundsComponent implements OnInit, OnDestroy {
  @Input() parentForm!: UntypedFormGroup;
  @Input() instructions$!: Observable<StandingInstruction[]>;
  @Input() directionSelection$!: Observable<'incoming' | 'outgoing' | ''>;
  directionSelection: string;
  enableUseSloaContainer: boolean = false;
  private destroy$ = new Subject<void>();
  cardModel: CardControl = {
    title: 'Source of funds',
    controlName: 'standingAuthId',
    radios: [],
    validationMessage: 'Select an account to receive funds',
  };
  selectedProfileKey: string;
  standingAuthCards: StandingAuthorizationCard[] = [];

  constructor(
    private actionCenterRedirectService: ActionCenterRedirectService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.store.select(selectStandingAuthId).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.selectedProfileKey = val
    });

    this.store.select(selectEnableUseSloaContainer).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.enableUseSloaContainer = val;    
    });

    this.directionSelection$.pipe(
      takeUntil(this.destroy$),
      filter(val => val !== ''),
      map((val) => {
        this.directionSelection = val;
        this.cardModel.title = this.isAchIn ? 'Source of funds' : 'Account to receive funds';
        this.cardModel.validationMessage = this.isAchIn ? 'Select a source of funds' : 'Select an account to receive funds';
        return val === transactionDirection.in.toLowerCase()
      }),
      mergeMap((showIncoming) => {
        if (showIncoming) {
          return this.instructions$?.pipe(
            map((instructions) => {
              return instructions.filter(instruction => instruction.fundsDirection === transactionDirection.in)
            })
          ) ?? []
        }
        return this.instructions$?.pipe(
          map((instructions) => {
            return instructions.filter(instruction => instruction.fundsDirection === transactionDirection.out)
          })
        ) ?? []
      }),
    ).subscribe((instructions) => {
        const newRadios: CardControlRadio[] = [];
        instructions.forEach((instruction) => {
          let isRecurring = instruction.frequencyDescription != null && instruction.frequencyDescription != undefined;
          let uniqueKey: string = `${instruction.transactionType[0]}-${instruction.cashieringRqstId}-${instruction.cashieringRqstSecondId}`;
          const labelArr: labelText[] = [];

          // only want to show additional details for the profile that's selected
          if ((uniqueKey === this.selectedProfileKey) && (instruction.abaNumber || instruction.accountName || instruction.accountType)) {
            labelArr.push({text: instruction.to1});
            labelArr.push({label: 'Account number: ', text: instruction.to2});
            if (instruction.abaNumber) labelArr.push({label: 'ABA number: ', text: instruction.abaNumber});
            if (instruction.accountName) labelArr.push({label: 'Account name: ', text: instruction.accountName});
            if (instruction.accountType) labelArr.push({label: 'Account type: ', text: instruction.accountType});
            if (instruction.nickname) labelArr.push({text: instruction.nickname});
            labelArr.push({text: `${isRecurring  ? 'Recurring' : 'On-Request'} - ${instruction.isThirdParty ? '3rd party' : '1st party'}`});
          }
          else {
            labelArr.push({text: instruction.to1});
            labelArr.push({label: 'Account number: ', text: instruction.to2});
            labelArr.push({text: `${isRecurring  ? 'Recurring' : 'On-Request'} - ${instruction.isThirdParty ? '3rd party' : '1st party'}`});
            if (instruction.nickname) labelArr.push({text: instruction.nickname});
          }

          newRadios.push({
            value: uniqueKey,
            label: labelArr,
          });
        });
        this.cardModel.radios = newRadios;
        if (this.enableUseSloaContainer && newRadios.length > 0) {
          this.updateStandingAuthorizationCards(newRadios);
        }
      }
    );
  }

  private updateStandingAuthorizationCards(radios: CardControlRadio[]): void {
    this.standingAuthCards = radios?.map((radio) => ({
      id: radio.value,
      title: radio.label?.[0]?.text || '',
      subtitle: radio.label?.[1] ? `${radio.label?.[1]?.label ?? ''}${radio.label?.[1]?.text ?? ''}` : '',
      details: (radio.label?.slice(2) || [])
        .map(item => `${item?.label ?? ''}${item?.text ?? ''}`)
        .filter(Boolean),
      selected: this.selectedProfileKey === radio.value
    }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  actionCenterRedirect() {
    this.actionCenterRedirectService.redirectToActionCenter();
  }

  get isAchIn(): boolean {
    return this.directionSelection === transactionDirection.in.toLowerCase();
  }

  onStandingAuthCardSelected(cardId: string): void {
    this.parentForm?.get('standingAuthId')?.setValue(cardId);
    this.parentForm?.get('standingAuthId')?.markAsTouched();
    this.selectedProfileKey = cardId;
    this.standingAuthCards = this.standingAuthCards.map(card => ({ ...card, selected: card.id === cardId }));
  }
}
